import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Drawer } from 'vaul'
import { Trophy, UserPlus, ArrowUpDown, Info, Gamepad2, BellOff, X, CheckCheck, Trash2, ChevronRight } from 'lucide-react'
import { staggerList, listItem, tapScale } from '@/lib/motion'
import { formatRelativeTime } from '@/lib/utils'
import { useNotificationStore, type NotificationType } from '@/stores/notificationStore'
import { EmptyState } from './EmptyState'

const typeConfig: Record<NotificationType, { icon: typeof Trophy; color: string; bg: string }> = {
  jackpot_win: { icon: Trophy, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10' },
  room_invite: { icon: UserPlus, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10' },
  transaction: { icon: ArrowUpDown, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10' },
  system: { icon: Info, color: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-500/10' },
  game: { icon: Gamepad2, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-500/10' },
}

interface NotificationPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NotificationPanel({ open, onOpenChange }: NotificationPanelProps) {
  const navigate = useNavigate()
  const notifications = useNotificationStore((s) => s.notifications)
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const markAsRead = useNotificationStore((s) => s.markAsRead)
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead)
  const dismiss = useNotificationStore((s) => s.dismiss)
  const clearAll = useNotificationStore((s) => s.clearAll)

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
          <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 max-h-[85vh] flex flex-col">
            {/* Drag handle */}
            <div className="flex justify-center py-3">
              <div className="w-10 h-1.5 rounded-full bg-foreground/20" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 pb-4">
              <div className="flex items-center gap-3">
                <Drawer.Title className="font-display text-2xl font-semibold text-foreground">
                  通知
                </Drawer.Title>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-bold">
                    {unreadCount}
                  </span>
                )}
              </div>
              {unreadCount > 0 && (
                <motion.button
                  whileTap={tapScale}
                  onClick={markAllAsRead}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-foreground/[0.04] hover:bg-foreground/[0.08] transition-colors"
                >
                  <CheckCheck className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">全部已讀</span>
                </motion.button>
              )}
            </div>

            <Drawer.Description className="sr-only">通知列表</Drawer.Description>

            {/* Notification list */}
            <div className="flex-1 overflow-y-auto px-6 pb-2">
              {notifications.length === 0 ? (
                <EmptyState
                  icon={BellOff}
                  title="沒有通知"
                  description="目前沒有任何通知。有新消息時會在此顯示。"
                  className="py-12"
                />
              ) : (
                <motion.div
                  variants={staggerList}
                  initial="hidden"
                  animate="visible"
                  className="space-y-2"
                >
                  {notifications.map((notification) => {
                    const config = typeConfig[notification.type]
                    const Icon = config.icon

                    return (
                      <motion.div
                        key={notification.id}
                        variants={listItem}
                        className={`relative flex items-start gap-3 p-3 rounded-2xl bg-foreground/[0.04] border transition-colors ${
                          notification.read
                            ? 'border-foreground/[0.06]'
                            : 'border-amber-400/20 bg-amber-400/[0.02]'
                        }`}
                        onClick={() => !notification.read && markAsRead(notification.id)}
                      >
                        {/* Unread dot */}
                        {!notification.read && (
                          <span className="absolute top-3 left-1 w-1.5 h-1.5 rounded-full bg-amber-400" />
                        )}

                        {/* Type icon */}
                        <div className={`flex-shrink-0 w-9 h-9 rounded-xl ${config.bg} flex items-center justify-center ml-1`}>
                          <Icon className={`w-4.5 h-4.5 ${config.color}`} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm font-medium leading-tight ${notification.read ? 'text-foreground/80' : 'text-foreground'}`}>
                              {notification.title}
                            </p>
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0 mt-0.5">
                              {formatRelativeTime(notification.timestamp)}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">
                            {notification.message}
                          </p>
                        </div>

                        {/* Dismiss button */}
                        <motion.button
                          whileTap={tapScale}
                          onClick={(e) => {
                            e.stopPropagation()
                            dismiss(notification.id)
                          }}
                          className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg hover:bg-foreground/[0.08] transition-colors mt-0.5"
                        >
                          <X className="w-3.5 h-3.5 text-muted-foreground/60" />
                        </motion.button>
                      </motion.div>
                    )
                  })}
                </motion.div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-foreground/[0.06] safe-area-bottom space-y-2">
              <motion.button
                whileTap={tapScale}
                onClick={() => {
                  onOpenChange(false)
                  navigate('/notifications')
                }}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-foreground/[0.04] hover:bg-foreground/[0.08] transition-colors"
              >
                <span className="text-sm text-foreground font-medium">查看全部紀錄</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </motion.button>
              {notifications.length > 0 && (
                <motion.button
                  whileTap={tapScale}
                  onClick={clearAll}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl hover:bg-rose-500/10 transition-colors"
                >
                  <Trash2 className="w-4 h-4 text-rose-400/70" />
                  <span className="text-sm text-rose-400/70 font-medium">清除所有通知</span>
                </motion.button>
              )}
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
